import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Plus, Sparkles, CheckCircle2, BookmarkPlus, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";
import MemoryManager from "../components/MemoryManager";
import { ListenButton, useIsPremium } from "../components/ListenButton";
import { MicButton } from "../components/MicButton";
import { Switch } from "../components/ui/switch";
import { playText, stopAudio, voiceErrorMessage } from "../lib/voice";

const STYLES = [
  { k: "nurturing_mentor", l: "Nurturing Mentor" },
  { k: "tough_love_coach", l: "Tough-Love Coach" },
  { k: "curious_investigator", l: "Curious Investigator" },
  { k: "mystical_guide", l: "Mystical Guide" },
];

function AlignMeter({ score }) {
  return (
    <div className="w-full">
      <div className="flex justify-between text-[11px] font-mono uppercase tracking-widest mb-1">
        <span className="text-accent">Not-self</span>
        <span className="text-primary">Signature · {score}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-accent to-primary" animate={{ width: `${score}%` }} transition={{ duration: 0.6 }} />
      </div>
    </div>
  );
}

export default function Companion() {
  const { profile } = useAuth();
  const pid = profile?.id;
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [style, setStyle] = useState(profile?.companion_style || "curious_investigator");
  const [memoryReload, setMemoryReload] = useState(0);
  const [savedMsgs, setSavedMsgs] = useState(new Set());
  const [voiceReplies, setVoiceReplies] = useState(true);
  const premium = useIsPremium();
  useEffect(() => stopAudio, []);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    if (!pid) return;
    const { data } = await api.get(`/profiles/${pid}/companion/sessions`);
    setSessions(data);
  }, [pid]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [active?.messages?.length, busy]);

  if (profile === null) return <Navigate to="/setup" replace />;

  const openSession = async (id) => {
    const { data } = await api.get(`/profiles/${pid}/companion/sessions/${id}`);
    setActive(data);
  };

  const start = async () => {
    setStarting(true);
    try {
      await api.patch(`/profiles/${pid}/companion-style`, { companion_style: style });
      const { data } = await api.post(`/profiles/${pid}/companion/sessions`);
      setActive(data);
      loadSessions();
    } catch { toast.error("Could not start session"); } finally { setStarting(false); }
  };

  const send = async (spoken) => {
    const text = (typeof spoken === "string" ? spoken : input).trim();
    if (!text || busy) return;
    if (typeof spoken !== "string") setInput("");
    setActive((a) => ({ ...a, messages: [...a.messages, { role: "user", content: text }] }));
    setBusy(true);
    try {
      const { data } = await api.post(`/profiles/${pid}/companion/sessions/${active.id}/chat`, { content: text });
      setActive((a) => ({ ...a, alignment_score: data.alignment_score, messages: [...a.messages, data] }));
      if (typeof spoken === "string" && voiceReplies) playText(data.content).catch(async (err) => toast.error(await voiceErrorMessage(err)));
    } catch (e) { toast.error(e.response?.data?.detail || "Message failed"); } finally { setBusy(false); }
  };

  const saveToMemory = async (content, key) => {
    if (savedMsgs.has(key)) return;
    try {
      await api.post(`/profiles/${pid}/memories`, { content: content.slice(0, 500), category: "other" });
      setSavedMsgs((s) => new Set(s).add(key));
      setMemoryReload((n) => n + 1);
      toast.success("Saved to your companion's memory");
    } catch { toast.error("Could not save to memory"); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/profiles/${pid}/companion/sessions/${active.id}/complete`);
      setActive((a) => ({ ...a, completed_at: new Date().toISOString(), summary: data.summary, teaching_reflection: data.teaching_reflection }));
      loadSessions();
      setMemoryReload((n) => n + 1);
      toast.success("Session complete. Insights saved to memory.");
    } catch { toast.error("Could not complete"); } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <div className="space-y-4">
          <div className="panel p-5">
            <p className="overline mb-3">Companion style</p>
            <div className="space-y-2">
              {STYLES.map((s) => (
                <button key={s.k} data-testid={`style-${s.k}`} onClick={() => setStyle(s.k)}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${style === s.k ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>{s.l}</button>
              ))}
            </div>
            <button data-testid="new-session-btn" onClick={start} disabled={starting || !pid}
              className="gold-btn w-full mt-4 py-2.5 rounded-full font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {starting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} New session
            </button>
          </div>
          <div className="panel p-5">
            <p className="overline mb-3">History</p>
            <div className="space-y-2 max-h-72 overflow-auto">
              {sessions.length ? sessions.map((s) => (
                <button key={s.id} data-testid={`session-${s.id}`} onClick={() => openSession(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${active?.id === s.id ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                  <div className="flex items-center gap-1.5">{s.completed_at && <CheckCircle2 size={13} className="text-primary" />}{s.dilemma_title}</div>
                  <div className="text-xs text-muted-foreground">alignment {s.alignment_score}</div>
                </button>
              )) : <p className="text-xs text-muted-foreground">No sessions yet.</p>}
            </div>
          </div>
          <MemoryManager profileId={pid} reloadSignal={memoryReload} />
        </div>

        {/* Chat */}
        <div className="panel flex flex-col min-h-[70vh]">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
              <Sparkles className="text-primary mb-4" size={28} />
              <h2 className="font-serif text-3xl mb-2">Your companion is listening.</h2>
              <p className="text-muted-foreground max-w-sm">Pick a style and start a session. It will surface a dilemma tuned to your design and guide you through it.</p>
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-border">
                <p className="font-serif text-xl mb-1">{active.dilemma_title}</p>
                <p className="text-sm text-muted-foreground mb-4">{active.dilemma_text}</p>
                <AlignMeter score={active.alignment_score} />
              </div>

              <div ref={scrollRef} className="flex-1 overflow-auto p-5 space-y-4" data-testid="chat-messages">
                {active.messages.map((m, i) => {
                  const key = `${active.id}-${i}`;
                  const saved = savedMsgs.has(key);
                  return (
                    <div key={key} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`group max-w-[80%] ${m.role === "user" ? "" : "relative"}`}>
                        <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-accent/20 border border-accent/30" : "glass"}`}>
                          {m.content}
                        </div>
                        {m.role !== "user" && (
                          <span className="inline-flex items-center gap-3">
                          <ListenButton text={m.content} testid={`listen-msg-${i}`} className="mt-1" />
                          <button data-testid={`memory-save-msg-${i}`} onClick={() => saveToMemory(m.content, key)}
                            title={saved ? "Saved to memory" : "Save to memory"}
                            className={`mt-1 inline-flex items-center gap-1 text-[11px] transition-colors ${saved ? "text-primary" : "text-muted-foreground/60 hover:text-primary"}`}>
                            {saved ? <Check size={12} /> : <BookmarkPlus size={12} />}
                            {saved ? "Saved" : "Remember this"}
                          </button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {busy && <div className="flex justify-start"><div className="glass px-4 py-3 rounded-2xl"><Loader2 size={16} className="animate-spin text-primary" /></div></div>}
              </div>

              {!active.completed_at && premium && (
                <label className="px-5 pt-3 flex items-center gap-2 text-xs text-muted-foreground border-t border-border">
                  <Switch checked={voiceReplies} onCheckedChange={setVoiceReplies} data-testid="voice-replies-toggle" />
                  Speak replies aloud when I talk
                </label>
              )}
              {active.completed_at ? (
                <div className="p-5 border-t border-border space-y-3">
                  <div><p className="overline mb-1">Summary</p><p className="text-sm text-foreground/90">{active.summary}</p></div>
                  <div><p className="overline mb-1">Teaching</p><p className="text-sm text-foreground/90">{active.teaching_reflection}</p></div>
                </div>
              ) : (
                <div className="p-4 border-t border-border flex gap-2">
                  <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Say what's on your mind…"
                    className="flex-1 px-4 py-3 rounded-full border border-border focus:border-primary outline-none" />
                  <MicButton disabled={busy} onTranscript={(t) => send(t)} />
                  <button data-testid="chat-send" onClick={() => send()} disabled={busy} className="gold-btn px-5 rounded-full flex items-center"><Send size={16} /></button>
                  <button data-testid="chat-complete" onClick={complete} disabled={busy} className="px-4 rounded-full border border-border hover:border-primary/50 text-sm transition-colors">End</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
